import { z } from 'zod';

import {
    BlobImageSchema,
    CostEntryType,
    CostEntryTypeSchema,
    ImageFormValueSchema,
} from '@/api/common/common.models';
import {
    ExternalCostAggregate,
    ExternalCostEntry,
} from '@/api/externalCostEntries/externalCostEntries.models';
import { ExternalTask } from '@/api/externalTasks/externalTasks.models';
import { Preliminary } from '@/api/preliminaries/preliminaries.models';
import {
    PreliminaryCostAggregate,
    PreliminaryCostEntry,
} from '@/api/preliminaryCostEntries/preliminaryCostEntries.models';
import { ProjectTask } from '@/api/projectTasks/projectTasks.models';
import {
    TaskCostAggregate,
    TaskCostEntry,
} from '@/api/taskCostEntries/taskCostEntries.models';
import { getTotalImagesSize } from '@/lib/file';

// Enum used only on frontend to differentiate between largely shared models of task and preliminary costs
// in order to simplify the form and validation logic. It is also used to direct API calls to separate endpoints.
export enum CostEntryParentType {
    Task = 'Task',
    Preliminary = 'Preliminary',
    ExternalTask = 'ExternalTask',
}

export const CostEntryParentTypeSchema = z.enum(CostEntryParentType);

export const GenericCostFormModelSchema = z.object({
    description: z
        .string()
        .min(1, { message: 'Description is required' })
        .max(200, {
            message: 'Description cannot be longer than 200 characters',
        }),
    cost: z
        .number({
            error: 'Cost is required',
        })
        .nonnegative({
            error: 'Cost cannot be negative',
        }),
});

export type GenericCostFormModel = z.infer<typeof GenericCostFormModelSchema>;

export const LabourCostFormModelSchema = z.object({
    operativeId: z.string().min(1, { message: 'Operative must be selected' }),
    hours: z
        .number({
            error: 'Hours must be a number',
        })
        .nonnegative({
            error: 'Hours cannot be negative',
        }),
    cost: z
        .number({
            error: 'Cost is required',
        })
        .nonnegative({
            error: 'Cost cannot be negative',
        }),
});

export type LabourCostFormModel = z.infer<typeof LabourCostFormModelSchema>;

export const MAX_TOTAL_IMAGES_SIZE_MB = 20;
const MAX_TOTAL_IMAGES_SIZE_BYTES = MAX_TOTAL_IMAGES_SIZE_MB * 1024 * 1024;

export const BaseCostEntryFormModelSchema = z.object({
    parentId: z.string().min(1, { message: 'This field is required' }),
    entryType: CostEntryTypeSchema,
    labourCosts: z.array(LabourCostFormModelSchema),
    materialCosts: z.array(GenericCostFormModelSchema),
    miscellaneousCosts: z.array(GenericCostFormModelSchema),
    images: z.array(ImageFormValueSchema).superRefine((images, ctx) => {
        const totalSize = getTotalImagesSize(images);
        if (totalSize > MAX_TOTAL_IMAGES_SIZE_BYTES) {
            ctx.addIssue({
                code: 'custom',
                message: `Your total image size exceeds the ${MAX_TOTAL_IMAGES_SIZE_MB}MB limit`,
            });
        }
    }),
});

export type BaseCostEntryFormModel = z.infer<
    typeof BaseCostEntryFormModelSchema
>;

export function refineCostArrays<T extends typeof BaseCostEntryFormModelSchema>(
    baseCostSchema: T,
) {
    return baseCostSchema
        .refine(
            (data) => {
                if (data.entryType !== CostEntryType.Labour) return true;
                return data.labourCosts.length > 0;
            },
            {
                message: 'At least one cost entry is required',
                path: ['labourCosts'],
            },
        )
        .refine(
            (data) => {
                if (data.entryType !== CostEntryType.Materials) return true;
                return data.materialCosts.length > 0;
            },
            {
                message: 'At least one cost entry is required',
                path: ['materialCosts'],
            },
        )
        .refine(
            (data) => {
                if (data.entryType !== CostEntryType.Miscellaneous) return true;
                return data.miscellaneousCosts.length > 0;
            },
            {
                message: 'At least one cost entry is required',
                path: ['miscellaneousCosts'],
            },
        );
}

export const CostSchema = z.object({
    operativeId: z.string().nullable(),
    operativeName: z.string().nullable(),
    hours: z
        .number({
            error: 'Hours are required',
        })
        .nonnegative({
            error: 'Hours cannot be negative',
        })
        .nullable(),
    description: z.string().nullable(),
    cost: z
        .number({
            error: 'Cost is required',
        })
        .nonnegative({
            error: 'Cost cannot be negative',
        }),
    images: z.array(BlobImageSchema),
});

export type Cost = z.infer<typeof CostSchema>;

export type CostEntry = (
    | TaskCostEntry
    | PreliminaryCostEntry
    | ExternalCostEntry
) & {
    parentType: CostEntryParentType;
};
export type CostEntryParent = ProjectTask | Preliminary | ExternalTask;
export type CostAggregate = (
    | TaskCostAggregate
    | PreliminaryCostAggregate
    | ExternalCostAggregate
) & {
    parentType: CostEntryParentType;
};
